// This class was inspired by https://github.com/wandb/wandb/blob/main/wandb/sdk/launch/github_reference.py
// Consider porting fixes / improvements over.

const PREFIX_HTTPS = 'https://';
const PREFIX_SSH = 'git@';
const SUFFIX_GIT = '.git';

export class GitHubReference {
  // Attempt to parse a string as a GitHub URL.
  static parse(uri: string): GitHubReference | null {
    // Special case: git@github.com:wandb/wandb.git
    const ref = new GitHubReference();
    if (uri.startsWith(PREFIX_SSH)) {
      const index = uri.indexOf(':', PREFIX_SSH.length);
      if (index > 0) {
        ref.host = uri.substring(PREFIX_SSH.length, index);
        const sshParts = uri.substring(index + 1).split('/');
        if (sshParts.length < 2) {
          return null;
        }
        const lastIdx = sshParts.length - 1;
        if (sshParts[lastIdx].endsWith(SUFFIX_GIT)) {
          sshParts[lastIdx] = sshParts[lastIdx].substring(
            0,
            sshParts[lastIdx].length - SUFFIX_GIT.length
          );
          ref.hasGitSuffix = true;
        }
        if (sshParts.length === 2) {
          ref.organization = sshParts[0];
          ref.repo = sshParts[1];
        } else if (sshParts.length === 3) {
          // Have not seen this case in the wild
          ref.version = sshParts[0];
          ref.organization = sshParts[1];
          ref.repo = sshParts[2];
        } else if (sshParts.length >= 4) {
          ref.version = sshParts[0];
          ref.organization = sshParts[1];
          ref.project = sshParts[2];
          ref.repo = sshParts[3];
          ref.path = sshParts.length > 4 ? sshParts.slice(4).join('/') : null;
        }
        return ref;
      } else {
        // Could not parse host name
        return null;
      }
    }

    const parsed = new URL(uri);
    if (parsed.protocol !== 'https:') {
      return null;
    }
    // Represent non-existent username/password as null instead of empty string
    ref.username = parsed.username || null;
    ref.password = parsed.password || null;
    ref.host = parsed.host;
    const parts = parsed.pathname.split('/');
    if (parts.length > 1) {
      if (parts[1] === 'orgs' && parts.length > 2) {
        ref.organization = parts[2];
      } else {
        ref.organization = parts[1];
        if (parts.length > 2) {
          ref.repo = parts[2];
          if (ref.repo.endsWith(SUFFIX_GIT)) {
            ref.repo = ref.repo.substring(
              0,
              ref.repo.length - SUFFIX_GIT.length
            );
          }
          ref.view = parts[3] || null;
          ref.path = parts[4] ? parts.slice(4).join('/') : null;
        }
      }
    }
    return ref;
  }

  username: string | null = null;
  password: string | null = null;
  host: string | null = null;

  version: string | null = null; // Used for Azure reference
  organization: string | null = null;
  project: string | null = null; // Used for Azure reference
  repo: string | null = null;
  hasGitSuffix: boolean = false;

  view: string | null = null; // tree or blob

  path: string | null = null;

  url_host(): string | null {
    if (this.host) {
      let auth = this.username ?? '';
      if (this.password) {
        auth += `:${this.password}`;
      }
      if (auth) {
        auth += '@';
      }
      return `${PREFIX_HTTPS}${auth}${this.host}`;
    }
    return null;
  }

  url_organization(): string | null {
    if (this.organization) {
      return `${this.url_host()}/${this.organization}`;
    }
    return null;
  }

  url_repo(): string | null {
    if (this.host && this.organization && this.repo) {
      return `${this.url_organization()}/${this.repo}`;
    }
    return null;
  }

  url(): string | null {
    let url = this.url_repo();
    if (this.view) {
      url += `/${this.view}`;
    }
    if (this.path) {
      url += `/${this.path}`;
    }
    return url;
  }

  repo_ssh(): string | null {
    if (this.host && this.organization && this.repo) {
      let uri = `${PREFIX_SSH}${this.host}:`;
      if (this.version) {
        uri += `${this.version}/`;
      }
      if (this.organization) {
        uri += `${this.organization}/`;
      }
      if (this.project) {
        uri += `${this.project}/`;
      }
      if (this.repo) {
        uri += `${this.repo}`;
      }
      if (this.path) {
        uri += `/${this.path}`;
      }
      if (this.hasGitSuffix) {
        uri += SUFFIX_GIT;
      }
      return uri;
    }
    return null;
  }
}
